import type { VideoCodec } from "mediabunny";
import { VIDEO_DIMENSIONS } from "../constants";
import type {
  ExportProgress,
  OutputSize,
  StudioState,
  VideoCapability
} from "../types";
import { createOutputCanvas, renderStudioFrame } from "./renderer";

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function fileStem(state: StudioState): string {
  return `wavelet-${state.template}-${state.seed.toString(16).padStart(8, "0")}`;
}

export async function exportPng(state: StudioState): Promise<void> {
  await document.fonts.ready;
  const canvas = createOutputCanvas(state);
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas export is unavailable in this browser.");
  renderStudioFrame(context, state, { progress: 0 });
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((result) => {
      if (result) resolve(result);
      else reject(new Error("The PNG could not be created."));
    }, "image/png");
  });
  downloadBlob(blob, `${fileStem(state)}.png`);
}

export async function detectVideoCapability(outputSize: OutputSize): Promise<VideoCapability> {
  const { width, height } = VIDEO_DIMENSIONS[outputSize];
  try {
    const { canEncodeVideo, QUALITY_HIGH } = await import("mediabunny");
    if (await canEncodeVideo("avc", { width, height, bitrate: QUALITY_HIGH })) {
      return { format: "mp4", codec: "avc", label: "H.264 MP4 · 60 fps" };
    }
    if (await canEncodeVideo("vp9", { width, height, bitrate: QUALITY_HIGH })) {
      return { format: "webm", codec: "vp9", label: "VP9 WebM · 60 fps" };
    }
    if (await canEncodeVideo("vp8", { width, height, bitrate: QUALITY_HIGH })) {
      return { format: "webm", codec: "vp8", label: "VP8 WebM · 60 fps" };
    }
  } catch {
    // MediaRecorder is checked below.
  }

  if (
    typeof MediaRecorder !== "undefined" &&
    typeof HTMLCanvasElement !== "undefined" &&
    "captureStream" in HTMLCanvasElement.prototype
  ) {
    const supported = ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"].some(
      (type) => MediaRecorder.isTypeSupported(type)
    );
    if (supported) {
      return {
        format: "webm",
        codec: "media-recorder",
        label: "WebM compatibility mode · realtime"
      };
    }
  }

  return {
    format: null,
    codec: null,
    label: "Video unavailable · PNG still works"
  };
}

function abortError(): DOMException {
  return new DOMException("Video export was cancelled.", "AbortError");
}

async function exportWithMediabunny(
  state: StudioState,
  capability: { format: "mp4" | "webm"; codec: VideoCodec },
  onProgress: (progress: ExportProgress) => void,
  signal: AbortSignal
): Promise<void> {
  const {
    BufferTarget,
    CanvasSource,
    Mp4OutputFormat,
    Output,
    QUALITY_HIGH,
    WebMOutputFormat
  } = await import("mediabunny");
  const canvas = createOutputCanvas(state, VIDEO_DIMENSIONS[state.output]);
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) throw new Error("Canvas export is unavailable in this browser.");

  const target = new BufferTarget();
  const format = capability.format === "mp4" ? new Mp4OutputFormat() : new WebMOutputFormat();
  const output = new Output({ format, target });
  const source = new CanvasSource(canvas, {
    codec: capability.codec as VideoCodec,
    bitrate: QUALITY_HIGH,
    keyFrameInterval: 2,
    latencyMode: "quality"
  });
  output.addVideoTrack(source, {
    name: "Wavelet Audio Brand Studio"
  });

  const totalFrames = state.animation.duration * state.animation.fps;
  onProgress({
    phase: "preparing",
    currentFrame: 0,
    totalFrames,
    format: capability.format
  });
  await output.start();

  try {
    for (let frame = 0; frame < totalFrames; frame += 1) {
      if (signal.aborted) throw abortError();
      const progress = frame / totalFrames;
      renderStudioFrame(context, state, { progress });
      await source.add(frame / state.animation.fps, 1 / state.animation.fps, {
        keyFrame: frame % (state.animation.fps * 2) === 0
      });
      if (frame % 6 === 0 || frame === totalFrames - 1) {
        onProgress({
          phase: "rendering",
          currentFrame: frame + 1,
          totalFrames,
          format: capability.format
        });
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      }
    }

    onProgress({
      phase: "finalizing",
      currentFrame: totalFrames,
      totalFrames,
      format: capability.format
    });
    if (signal.aborted) throw abortError();
    await output.finalize();
    if (signal.aborted) throw abortError();
  } catch (error) {
    if (output.state === "started") await output.cancel();
    throw error;
  }

  if (!target.buffer) throw new Error("The video encoder returned an empty file.");
  const mime = capability.format === "mp4" ? "video/mp4" : "video/webm";
  const blob = new Blob([target.buffer], { type: mime });
  downloadBlob(blob, `${fileStem(state)}.${capability.format}`);
  onProgress({
    phase: "done",
    currentFrame: totalFrames,
    totalFrames,
    format: capability.format
  });
}

async function exportWithMediaRecorder(
  state: StudioState,
  onProgress: (progress: ExportProgress) => void,
  signal: AbortSignal
): Promise<void> {
  const canvas = createOutputCanvas(state, VIDEO_DIMENSIONS[state.output]);
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) throw new Error("Canvas export is unavailable in this browser.");
  const stream = canvas.captureStream(state.animation.fps);
  const mimeType =
    ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"].find((type) =>
      MediaRecorder.isTypeSupported(type)
    ) ?? "video/webm";
  const recorder = new MediaRecorder(stream, {
    mimeType,
    videoBitsPerSecond: state.output === "wide" ? 28_000_000 : 22_000_000
  });
  const chunks: BlobPart[] = [];
  recorder.addEventListener("dataavailable", (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  });
  const totalFrames = state.animation.duration * state.animation.fps;

  const complete = new Promise<void>((resolve, reject) => {
    recorder.addEventListener("stop", () => resolve(), { once: true });
    recorder.addEventListener("error", () => reject(new Error("MediaRecorder failed.")), {
      once: true
    });
  });

  const stopRecorder = () => {
    if (recorder.state !== "inactive") recorder.stop();
  };
  signal.addEventListener("abort", stopRecorder, { once: true });

  try {
    recorder.start(1000);
    const startedAt = performance.now();
    onProgress({ phase: "rendering", currentFrame: 0, totalFrames, format: "webm" });

    await new Promise<void>((resolve, reject) => {
      const tick = (now: number) => {
        if (signal.aborted) {
          stopRecorder();
          reject(abortError());
          return;
        }
        const elapsed = (now - startedAt) / 1000;
        const progress = Math.min(1, elapsed / state.animation.duration);
        renderStudioFrame(context, state, { progress: progress % 1 });
        onProgress({
          phase: "rendering",
          currentFrame: Math.min(totalFrames, Math.round(progress * totalFrames)),
          totalFrames,
          format: "webm"
        });
        if (progress >= 1) {
          stopRecorder();
          resolve();
        } else {
          requestAnimationFrame(tick);
        }
      };
      requestAnimationFrame(tick);
    });

    await complete;
    if (signal.aborted) throw abortError();
    const blob = new Blob(chunks, { type: mimeType });
    downloadBlob(blob, `${fileStem(state)}.webm`);
    onProgress({ phase: "done", currentFrame: totalFrames, totalFrames, format: "webm" });
  } finally {
    signal.removeEventListener("abort", stopRecorder);
    stopRecorder();
    stream.getTracks().forEach((track) => track.stop());
  }
}

export async function exportVideo(
  state: StudioState,
  capability: VideoCapability,
  onProgress: (progress: ExportProgress) => void,
  signal: AbortSignal
): Promise<void> {
  await document.fonts.ready;
  if (!capability.format || !capability.codec) {
    throw new Error("This browser cannot encode video. Export a PNG instead.");
  }

  if (capability.codec === "media-recorder") {
    await exportWithMediaRecorder(state, onProgress, signal);
    return;
  }

  await exportWithMediabunny(
    state,
    { format: capability.format, codec: capability.codec as VideoCodec },
    onProgress,
    signal
  );
}
